import axios from 'axios'
import { PDFDocument } from '@cantoo/pdf-lib'
import { v4 as uuidv4 } from 'uuid'

import { OrderModalValues, SignDocumentTabOptions } from '@/types/order'

export const fetcher = async (
  url: string,
  method?: string,
  data?: any,
): Promise<any> => {
  try {
    const response = await axios({
      method: method ?? 'GET',
      url,
      data,
      timeout: 30000,
    })
    return response.data
  } catch (error) {
    console.error('Fetcher error:', error)

    if (axios.isAxiosError(error)) {
      const errorMessage =
        error.response?.data?.error || error.message || 'Request failed'
      const statusCode = error.response?.status || 500

      throw new Error(`${errorMessage} (Status: ${statusCode})`)
    }

    throw new Error(
      error instanceof Error ? error.message : 'Unknown error occurred',
    )
  }
}

/**
 * Slice an array into chunks of a given size
 * @param arr The array to slice
 * @param n The size of the chunks
 * @returns An array of arrays of size n
 */
export const chunks = (arr: any[], n: number): any[][] => {
  const chunks: any[][] = []
  for (let i = 0; i < arr.length; i += n) {
    chunks.push(arr.slice(i, i + n))
  }
  return chunks
}

/**
 * Deduplicate an array of objects based on a given field
 * @param array The array to deduplicate
 * @param field The field to use to deduplicate
 * @returns The deduplicated array
 */
export const deduplicate = <T,>(array: T[], field: string): T[] => {
  return array?.filter(
    (value, index, self) =>
      index === self.findIndex((t) => t[field] === value[field]),
  )
}

/**
 * Sort an array of objects based on a given key and direction
 * @param array The array to sort
 * @param sortKey The key to use to sort
 * @param direction The direction to use to sort
 * @returns The sorted array
 */
export const sortArray = (
  array: any[],
  sortKey: string = 'confidence',
  direction: string = 'DESC',
): any[] => {
  return array.slice().sort(function (a, b) {
    const c = a[sortKey] || 0
    const d = b[sortKey] || 0
    return direction == 'ASC' ? c - d : d - c
  })
}

/**
 * Sort an array of strings based on a given key and direction
 * @param array The array to sort
 * @param sortKey The key to use to sort
 * @param direction The direction to use to sort
 * @returns The sorted array
 */
export const sortStringArray = <T,>(
  array: T[],
  sortKey: string = 'label',
  direction: string = 'ASC',
): T[] => {
  return array.slice().sort(function (a, b) {
    const c = a[sortKey] || ''
    const d = b[sortKey] || ''
    return direction == 'ASC' ? c.localeCompare(d) : d.localeCompare(c)
  })
}

/**
 * Returns a random integer between min and max
 * @param min The minimum value
 * @param max The maximum value
 * @returns The random integer generated
 */
const randomInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min

/**
 * Converts an HSL color to a HEX color
 * @param h The hue
 * @param s The saturation
 * @param l The lightness
 * @returns The HEX color generated
 */
const hslToHex = (h: number, s: number, l: number): string => {
  l /= 100
  const a = (s * Math.min(l, 1 - l)) / 100
  const f = (n) => {
    const k = (n + h / 30) % 12
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0')
  }
  return `#${f(0)}${f(8)}${f(4)}`
}

/**
 * Return a random HEX color
 * @returns A random HEX color
 */
export const randomColor = (): string => {
  const h = randomInt(0, 360)
  const s = randomInt(80, 100)
  const l = randomInt(50, 70)
  return hslToHex(h, s, l)
}

export type Item = {
  [key: string]: any
}

/**
 * Recursively get items from an array of items, now used for organization subteams
 * @param items The array of items to get the items from
 * @param key The key to use to get the items
 * @param result The result array to fill with the items
 * @returns The result array filled with the items
 */
export const recursivileyGetItems = (
  items: Item[],
  key: string,
  result: Item[] = [],
): Item[] => {
  if (!items) return result

  for (const item of items) {
    result.push(item)
    if (item[key]?.length) {
      recursivileyGetItems(item[key], key, result)
    }
  }
  return result
}
/**
 * Sort an array of objects by ascending or descending order regarding a key
 * @param array The array to sort
 * @param sortKey The key to sort by
 * @param direction Ascending or descending
 * @returns The sorted array
 */
export const sortDate = <T extends { [key: string]: any }>(
  array: T[],
  sortKey: string = 'updatedAt',
  direction: string = 'ASC',
): T[] =>
  array.slice().sort(function (a, b) {
    const c = new Date(a[sortKey])
    const d = new Date(b[sortKey])
    return direction === 'ASC'
      ? c.getTime() - d.getTime()
      : d.getTime() - c.getTime()
  })

export const getPercentage = (value: number, total: number): number =>
  Math.ceil((value / total) * 100)

export const cleanInput = ({
  input,
  keysToRemove,
}: {
  input: any
  keysToRemove: string[]
}) =>
  // Loop through input to ensure to only store on first level supported fields
  Object.keys(input).reduce((acc, field) => {
    if (
      !keysToRemove.includes(field) &&
      !field.includes('_') &&
      !field.includes('.')
    ) {
      return {
        ...acc,
        [field]: input[field],
      }
    }
    return acc
  }, {})

/**
 * Converts the given input into a base64-encoded string.
 * @param input - The input to convert. Can be either:
 *  - A Blob representing a binary file.
 *  - A string URL pointing to a resource that will be fetched.
 * @returns A Promise that resolves to a base64-encoded string (without the "data:" prefix).
 * @throws Will throw an error if:
 *  - The fetch request fails when `input` is a URL.
 *  - The FileReader fails to read the blob.
 */
export const getBase64 = async (input: string | Blob): Promise<string> => {
  let blob: Blob

  if (typeof input === 'string') {
    const response = await fetch(input)
    if (!response.ok) {
      throw new Error(`Failed to fetch resource: ${response.statusText}`)
    }
    blob = await response.blob()
  } else {
    blob = input
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      if (typeof reader.result === 'string') {
        const base64Data = reader.result.split(',')[1]
        resolve(base64Data)
      } else {
        reject(new Error('Failed to convert blob to base64'))
      }
    }

    reader.onerror = (error) =>
      reject(new Error(`Failed to read blob: ${error}`))
    reader.readAsDataURL(blob)
  })
}

/**
 * Splits a list of items into pages with a specified number of items per page.
 *
 * @param items - The array of items to be split into pages.
 * @param firstPageItemsLength - The number of items to include on the first page.
 * @param otherPagesItemsLength - The number of items to include on each subsequent page.
 * @param lastPageItemsLength - The number of items to remove from the last pagea and add to the next page
 * @returns An array of pages, where each page is an array of items.
 */
export const splitItemsPerPage = ({
  items,
  firstPageItemsLength,
  otherPagesItemsLength,
  lastPageItemsLength,
}: {
  items: any[]
  firstPageItemsLength: number
  otherPagesItemsLength: number
  lastPageItemsLength?: number
}) => {
  const pages: any = []
  pages.push(items.slice(0, firstPageItemsLength))

  for (
    let i = firstPageItemsLength;
    i < items.length;
    i += otherPagesItemsLength
  ) {
    const lastIndex = i + otherPagesItemsLength
    pages.push(items.slice(i, lastIndex))
  }

  if (lastPageItemsLength && lastPageItemsLength > 0) {
    const lastPage = pages[pages.length - 1]
    const productsShouldBeOnLastPage =
      lastPage.length > lastPageItemsLength
        ? pages[pages.length - 1].splice(lastPageItemsLength, lastPage.length)
        : []

    productsShouldBeOnLastPage.length > 0 &&
      pages.push(productsShouldBeOnLastPage)
  }

  return pages
}

/**
 * Returns the number of items to display on the first page
 * @param showSignAndConditions Whether to show the sign and conditions
 * @param itemsLength The number of items in the list
 * @returns The number of items to display on the first page
 */
export const firstPdfPageItems = ({
  itemsLength,
  showSignAndConditions,
}: {
  itemsLength: number
  showSignAndConditions: boolean
}) => {
  switch (true) {
    case showSignAndConditions && itemsLength <= 18:
      return 5
    case showSignAndConditions && itemsLength > 18:
      return 18
    case !showSignAndConditions && itemsLength <= 18:
      return 15
    case !showSignAndConditions && itemsLength > 18:
      return 18
    default:
      return 16
  }
}

/**
 * This function checks if the referer is valid and if the environment variables are set and can be used only to check referer or environment variables
 * @param request request object
 * @param variables array of environment variables to check
 * @param refererUrlToCheck the referer url to check
 * @returns  if the referer is invalid or the environment variables are not set, this function throw error
 */
export const checkRefererAndVariables = ({
  request,
  variables,
  refererUrlToCheck,
}: {
  request: Request
  variables?: string[]
  refererUrlToCheck?: string
}) => {
  if (variables?.length) {
    const undefinedVariables = variables.filter((variable) => !variable)
    if (undefinedVariables.length > 0) {
      return new Response(
        JSON.stringify({
          status: 400,
          message: 'Environment variables are not set',
        }),
        { status: 400, headers: { 'Content-Type': 'application/json' } },
      )
    }
  }

  if (refererUrlToCheck) {
    const referer = request.headers.get('referer')
    if (!referer?.includes(refererUrlToCheck)) {
      return new Response(
        JSON.stringify({
          status: 400,
          message: 'Invalid origin, request denied',
        }),
        { status: 400, headers: { 'Content-Type': 'application/json' } },
      )
    }
  }
}

/**
 * Slugify variant for properties custom keys or formulas
 * @param text The text to convert to a slug
 * @param maxLength Maximum length of the resulting slug
 * @returns The slugified text
 */
export const slugify = (text: string, maxLength: number = 24): string => {
  return (
    text
      .toLowerCase()
      .trim()
      // Normalize accented characters to their ASCII equivalents
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics (accents)
      .replace(/[^\w\s-]/g, '') // Remove remaining special characters
      .replace(/[\s-]+/g, '_') // Replace spaces and hyphens with underscores
      .replace(/_+/g, '_') // Replace multiple underscores with single underscore
      .replace(/^_+|_+$/g, '') // Remove underscores from start and end
      .substring(0, maxLength) // Limit to maxLength characters
      .replace(/_+$/g, '') // Remove any trailing underscore that might appear after truncation
  )
}

/**
 * Format a number for currency display
 * @param value The value to format (can be a number, string with currency sign, or number as string)
 * @param currencySign The currency sign to append (€, $, etc.)
 * @returns Formatted number with 2 decimal places and currency sign
 */
export const formatCurrency = (
  value: number | string | undefined,
  currencySign: string = '€',
): string => {
  if (value === undefined || value === null) {
    return `0,00 ${currencySign}`
  }
  // Convert to string if it's not already
  const valueStr = value.toString()
  // Remove any existing currency signs and non-numeric characters except dots and commas
  const cleanValue = valueStr
    .replace(/[^\d.,]/g, '')
    // Replace comma with dot for calculation
    .replace(',', '.')
  // Parse to float and handle NaN
  const numValue = parseFloat(cleanValue)
  if (isNaN(numValue)) {
    return `0,00 ${currencySign}`
  }
  // Format with 2 decimal places and comma as decimal separator
  return `${numValue.toFixed(2).replace('.', ',')} ${currencySign}`
}

export const formatPath = (product: {
  shop?: { slug?: string }
  slug?: string
}) => {
  if (!product.shop?.slug) return '#'
  if (product.shop?.slug && !product.slug)
    return `${process.env.NEXT_PUBLIC_APP_URL}/${product.shop?.slug}`
  return `${process.env.NEXT_PUBLIC_APP_URL}/${product.shop?.slug}/p/${product?.slug}`
}

/**
 * Format a number to 2 decimal places
 * @param value The value to format
 * @param currencySign The currency sign to append (€, $, etc.)
 * @param precision The precision of the number
 * @param commaSeparator Whether to use a comma as decimal separator
 * @returns If the value is a number, it will be formatted to 2 decimal places, if it's a string, it will be formatted to 2 decimal places and remove any spaces
 */
export const numberFormatter = ({
  value,
  currencySign,
  precision,
  commaSeparator,
}: {
  value: number | string | undefined
  currencySign?: string
  precision?: number
  commaSeparator?: boolean
}) => {
  return typeof value === 'number'
    ? (value || 0)
        .toFixed(precision || 2)
        .replace('.', commaSeparator ? ',' : '.') +
        (currencySign ? ` ${currencySign}` : '')
    : parseFloat(String(value || 0).replace(/\s+/g, ''))
        .toFixed(precision || 2)
        .replace('.', commaSeparator ? ',' : '.') +
        (currencySign ? ` ${currencySign}` : '')
}

/**
 * Transforms a number or a string in Anglo-Saxon format (e.g., "1,000.00")
 * into a continental European style format (e.g., "1.000,00").
 * @param {string | number} input - The number or string to format.
 * @returns {string} The formatted number as a string.
 */
export const formatNumber = (
  input: string | number | undefined | null,
): string => {
  if (input === null || input === undefined) return ''

  let number: number

  if (typeof input === 'string') {
    // Remove thousand separators and parse as float
    const sanitizedInput = input.replace(/,/g, '')
    number = parseFloat(sanitizedInput)
  } else {
    number = input
  }

  if (isNaN(number)) return ''

  // 'de-DE' locale uses '.' for thousand separators and ',' for decimal.
  return number.toLocaleString('de-DE', {
    maximumFractionDigits: 2,
  })
}

/**
 * Format an address
 * @param address The address to format
 * @param zipcode The zipcode to format
 * @param city The city to format
 * @param country The country to format
 * @returns The formatted address with the zipcode, city and country if they are provided
 */
export const formatAddress = ({
  address,
  zipcode,
  city,
  country,
  separatedLines = false,
}: {
  address?: string
  zipcode?: string
  city?: string
  country?: string
  separatedLines?: boolean
}) => {
  if (!address && !zipcode && !city && !country) return null

  if (separatedLines) {
    const firstLine = address || ''
    const secondLineParts = [zipcode, city, country].filter(Boolean)
    const secondLine = secondLineParts.join(', ')

    return firstLine + (secondLine ? '\n' + secondLine : '')
  }

  const addressParts = [address, zipcode, city, country].filter(Boolean)
  return addressParts.join(', ')
}

/**
 * Invoice number generator
 * @param id The id of the order
 * @returns The invoice number
 */
export const invoiceNumberGenerator = (id: string | undefined) => {
  if (!id) return uuidv4().split('-')[0].toUpperCase()
  return id.split('-')[0].toUpperCase()
}

/**
 * Merge an array of pdf inputs into a single pdf
 * @param pdfInputs The array of pdf inputs
 * @returns
 */
export const mergeArrayBuffers = async (
  pdfInputs: Buffer[],
): Promise<Buffer> => {
  const pdfDoc = await PDFDocument.create()

  try {
    for (const pdfInput of pdfInputs) {
      const pdfBuffer =
        typeof pdfInput === 'string'
          ? Buffer.from(pdfInput, 'base64')
          : pdfInput

      const pdf = await PDFDocument.load(pdfBuffer, {
        ignoreEncryption: true,
        password: '',
      })

      const copiedPages = await pdfDoc.copyPages(pdf, pdf.getPageIndices())
      copiedPages.forEach((page) => pdfDoc.addPage(page))
    }

    const mergedPdfFile = await pdfDoc.save()
    return Buffer.from(mergedPdfFile)
  } catch (error) {
    console.error('Error merging PDFs:', error)
    throw error
  }
}

/**
 * Format a weight
 * @param weight
 * @returns
 */
export const formatWeight = (weight: number | undefined) => {
  if (!weight) return '-'
  if (weight === 0) return '-'
  if (weight < 1000) return weight.toString() + ' kg'
  if (weight >= 1000) return (weight / 1000).toFixed(1) + ' t'
  return '-'
}

/**
 * Remove null values from an object
 * @param obj The object to remove null values from
 * @returns The object with null values removed
 */
export const removeNullValues = <T extends Record<string, unknown>>(
  obj: T,
): Partial<T> => {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== null && value !== undefined) {
      acc[key as keyof T] = value as T[keyof T]
    }
    return acc
  }, {} as Partial<T>)
}

/**
 * Parse a currency string to a number
 * @param currencyString The currency string to parse
 * @returns The number parsed from the currency string
 */
export const parseCurrencyToNumber = (
  currencyString: string | undefined,
): number => {
  if (!currencyString) return 0
  // Remove currency symbols, spaces, and replace comma with dot
  const cleaned = currencyString
    .replace(/€/g, '')
    .replace(/\s/g, '')
    .replace(/,/g, '.')
    .trim()
  return Number(cleaned) || 0
}

/**
 * Format a document filename
 * @param invoiceNumber The invoice number
 * @param invoiceAsQuote Whether the invoice is a quote
 * @param isTransferVoucher Whether the invoice is a transfer voucher
 * @returns The formatted document filename
 */
export const formatDocumentFilename = (
  invoiceNumber: string | undefined,
  invoiceAsQuote?: boolean,
  isTransferVoucher?: boolean,
): string => {
  let documentFilename = 'skop-document'
  const safeInvoiceNumber =
    (invoiceNumber
      ?.trim()
      .replace(/[\r\n]+/g, '')
      .replace(/[^a-z0-9._-]+/gi, '-')
      .replace(/^-+|-+$/g, '') ??
      '') ||
    ''
  if (safeInvoiceNumber) {
    if (invoiceAsQuote && invoiceAsQuote === true) {
      documentFilename = `skop-quote-${safeInvoiceNumber}`
    } else if (isTransferVoucher && isTransferVoucher === true) {
      documentFilename = `skop-transfer-voucher-${safeInvoiceNumber}`
    } else {
      documentFilename = `skop-invoice-${safeInvoiceNumber}`
    }
  }

  return documentFilename
}

/** Truthy string values for envelope.formValues.isTransferVoucher (case-insensitive). */
const TRANSFER_VOUCHER_TRUTHY_STRINGS = ['true', '1', 'yes'] as const

/**
 * Keywords that indicate a transfer voucher when present in envelope.title.
 * Case-insensitive match; add localized variants here (e.g. "bon de cession", "transfer voucher").
 */
export const TRANSFER_VOUCHER_KEYWORDS = ['bon de cession'] as const

/**
 * Get the list of keywords used to detect transfer vouchers from envelope.title.
 * Use this or extend TRANSFER_VOUCHER_KEYWORDS when adding localized variants.
 */
export const getTransferVoucherKeywords = (): readonly string[] => {
  return TRANSFER_VOUCHER_KEYWORDS
}

const isTransferVoucherFormValue = (
  value: string | boolean | undefined,
): boolean => {
  if (value === true) return true
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return TRANSFER_VOUCHER_TRUTHY_STRINGS.some((s) => normalized === s)
}

const titleMatchesTransferVoucherKeywords = (
  title: string | undefined,
): boolean => {
  if (!title?.trim()) return false
  const lower = title.toLowerCase()
  return TRANSFER_VOUCHER_KEYWORDS.some((kw) =>
    lower.includes(kw.toLowerCase()),
  )
}

/**
 * Determine if a signed document (from Documenso envelope) is a transfer voucher
 * based on stored formValues or envelope title.
 * @param envelope Documenso envelope data (may have formValues, title)
 * @returns true if the signed document was created as a transfer voucher
 */
export const getSignedDocumentIsTransferVoucher = (envelope: {
  formValues?: { isTransferVoucher?: string | boolean }
  title?: string
}): boolean => {
  if (!envelope) return false
  if (isTransferVoucherFormValue(envelope.formValues?.isTransferVoucher))
    return true
  if (titleMatchesTransferVoucherKeywords(envelope.title)) return true
  return false
}

/**
 * Get a GraphQL error message from an error object
 * @param e The error object
 * @returns The GraphQL error message
 */
export const getGraphQLErrorMessage = (e: unknown): string => {
  const err = e as { errors?: Array<{ message?: string }>; message?: string }
  if (err?.errors?.[0]?.message) return err.errors[0].message
  if (err instanceof Error) return err.message
  return typeof err?.message === 'string' ? err.message : 'Update order failed'
}

/**
 * Compares two objects deeply to determine if they are identical.
 * Returns true if there are changes (not equal), false if they are the same.
 */
export const hasChanges = (obj1: any, obj2: any) => {
  if (obj1 === obj2) return false

  if (
    typeof obj1 !== 'object' ||
    obj1 === null ||
    typeof obj2 !== 'object' ||
    obj2 === null
  ) {
    return true
  }

  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  if (keys1.length !== keys2.length) return true

  for (const key of keys1) {
    if (!keys2.includes(key)) return true

    if (hasChanges(obj1[key], obj2[key])) {
      return true
    }
  }

  return false
}

/**
 * Build a viewer key
 * @param order The order
 * @param options The options
 * @param twoSidesSign Whether the two sides sign is enabled
 * @returns The viewer key
 */
export const buildViewerKey = ({
  order,
  options,
  twoSidesSign,
}: {
  order: OrderModalValues
  options?: SignDocumentTabOptions
  twoSidesSign?: boolean
}) => {
  const itemsKey =
    order?.items
      ?.map(
        (item) =>
          `${item.id}:${item.quantity}:${item.rawPrice ?? item.price ?? 0}`,
      )
      .join('|') || 'no-items'

  return [
    order?.id ?? 'new-order',
    options?.isTransferVoucher ? 'transfer-voucher' : 'invoice',
    twoSidesSign ? 'two-sides' : 'one-side',
    itemsKey,
  ].join('|')
}

/**
 * Convert a value to a number
 * @param value The value to convert to a number
 * @returns The number converted from the value
 */
export const toNumber = (value: unknown): number => Number(value) || 0

/**
 * Normalize a string value
 * @param value The value to normalize (accepts null/undefined)
 * @returns The normalized value
 */
export const normalizeStringValue = (
  value: string | null | undefined,
): string => value?.trim()?.toLocaleLowerCase() ?? ''
